/**
 * Client-side 3-state presence reporter + consumer (mounted once in
 * MainLayout).
 *
 * Activity model (BA-approved defaults):
 *  - any interaction (pointer/keyboard/touch/wheel/navigation) ⇒ `online`
 *  - {@link AWAY_AFTER_MS} without interaction ⇒ `away` ("online but not
 *    active", AC-8)
 *  - {@link OFFLINE_AFTER_MS} without interaction ⇒ our presence key is
 *    untracked (others see `offline`, AC-9) while we STAY subscribed so we
 *    still see everyone else; renewed activity re-tracks `online` (AC-11)
 *  - app close / connectivity loss ⇒ Supabase expires the key on socket
 *    heartbeat loss ⇒ others see offline within ~60 s (AC-10)
 *
 * `track` is only called on state CHANGES (propagates in ~1 s — well inside
 * the 10 s tolerance of AC-11). Note: background-tab timer throttling may
 * delay the away/offline transitions by up to ~1 min — accepted by the BA
 * tolerances.
 *
 * @module features/p2p-chat/hooks/useSupabaseFamilyPresence
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useRealtimeScope } from '@/shared/hooks/useRealtimeScope';
import { isRealtimeEnabled } from '@/shared/lib/env';
import { subscribeActiveFamilyChange } from '@/shared/lib/familyContext';
import {
  subscribeFamilyPresence,
  type FamilyPresenceHandle,
  type TrackablePresenceStatus,
} from '../lib/familyPresence';
import { useFamilyPresenceStore } from '../stores/familyPresenceStore';

/** No interaction for this long ⇒ away (BA default; must stay < offline). */
export const AWAY_AFTER_MS = 60_000;
/** No interaction for this long ⇒ offline ("5 mins of inactive"). */
export const OFFLINE_AFTER_MS = 300_000;
/** How often the idle state machine re-evaluates. */
const IDLE_TICK_MS = 5_000;
/** Delay before re-subscribing after the channel is closed underneath us. */
const RESUBSCRIBE_DELAY_MS = 1_000;

/** DOM events that count as user interaction. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'wheel',
] as const;

export function useSupabaseFamilyPresence(): void {
  const { familyId, profileId } = useRealtimeScope();
  const location = useLocation();

  // Route changes count as activity (navigation = interaction). A ref bridge
  // lets the main effect read the latest activity without re-subscribing.
  const lastActivityRef = useRef<number>(Date.now());
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, [location]);

  useEffect(() => {
    if (!isRealtimeEnabled || familyId === null || profileId === null) {
      return;
    }

    let disposed = false;
    let handle: FamilyPresenceHandle | null = null;
    let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
    /** Our current reported phase; transitions gate `track`/`untrack` calls. */
    let phase: 'online' | 'away' | 'offline' = 'online';

    const store = useFamilyPresenceStore.getState();

    /**
     * True once the family context has announced a DIFFERENT active family
     * than the one this effect closed over.
     *
     * `subscribe` is called a second time from `onClosed`, after
     * {@link RESUBSCRIBE_DELAY_MS}, with the `familyId` captured above. That is
     * the right id for a plain socket drop, but `onClosed` is reached almost
     * exclusively via a GLOBAL teardown — a family switch or a sign-out — and
     * inside that window the id can go stale. The rescoping actions in
     * `familyStore` run two or three sequential round trips between clearing
     * the family context and restoring it, so React may not have re-rendered
     * this hook with the new scope (which is what would set `disposed`) before
     * the timer fires. Re-opening a presence channel on the family the user
     * has just left publishes their key to a roster they are no longer in.
     *
     * Reading the family context directly is what makes that independent of
     * the delay: this hook's `RESUBSCRIBE_DELAY_MS` and `useRealtimeScope`'s
     * `FAMILY_SWITCH_SETTLE_MS` are deliberately the same value, but nothing
     * enforces that and correctness must not rest on it. A `null` broadcast is
     * ignored on purpose — it means "unknown, ask again", which every rescope
     * emits mid-flight; only a different non-null family proves us stale, and
     * once it does, the effect re-run that follows owns the new scope.
     */
    let familyIdIsStale = false;
    const unsubscribeScope = subscribeActiveFamilyChange((activeFamilyId) => {
      if (activeFamilyId !== null && activeFamilyId !== familyId) {
        familyIdIsStale = true;
      }
    });

    const report = (status: TrackablePresenceStatus): void => {
      handle?.trackStatus(status);
      useFamilyPresenceStore.getState().setMyStatus(status);
    };

    const subscribe = (): void => {
      if (disposed || familyIdIsStale) return;
      handle = subscribeFamilyPresence({
        familyId,
        profileId,
        onStatuses: (statuses) => {
          useFamilyPresenceStore.getState().setStatuses(statuses);
        },
        onReady: () => {
          // (Re)joined — re-track the current non-offline phase.
          if (phase !== 'offline') {
            report(phase);
          }
        },
        onClosed: () => {
          // Channel was removed by a global realtime teardown (e.g. family
          // switch orchestration) — re-subscribe once things settle.
          // `subscribe` re-checks the family id at fire time, so this delay can
          // never re-open a channel on a family we have since left.
          if (disposed) return;
          resubscribeTimer = setTimeout(subscribe, RESUBSCRIBE_DELAY_MS);
        },
      });
      handle.trackStatus(phase === 'offline' ? 'online' : phase);
    };

    // ---- interaction listeners (passive; cheap ref write + rising edge) ----
    const onActivity = (): void => {
      lastActivityRef.current = Date.now();
      if (phase !== 'online') {
        phase = 'online';
        report('online');
      }
    };
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    // ---- idle state machine (checks every IDLE_TICK_MS) ----
    const tick = (): void => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= OFFLINE_AFTER_MS) {
        if (phase !== 'offline') {
          phase = 'offline';
          handle?.untrack();
          useFamilyPresenceStore.getState().setMyStatus('offline');
        }
      } else if (idleFor >= AWAY_AFTER_MS) {
        if (phase === 'online') {
          phase = 'away';
          report('away');
        }
      }
    };
    const idleTimer = setInterval(tick, IDLE_TICK_MS);

    // Initial state: mounting counts as activity.
    lastActivityRef.current = Date.now();
    store.setMyStatus('online');
    subscribe();

    return () => {
      disposed = true;
      unsubscribeScope();
      clearInterval(idleTimer);
      if (resubscribeTimer !== null) {
        clearTimeout(resubscribeTimer);
      }
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      handle?.untrack();
      handle?.teardown();
      useFamilyPresenceStore.getState().reset();
    };
  }, [familyId, profileId]);
}
